import React, { useEffect, useRef, useState } from 'react';
import ModalManager from '@restart/ui/ModalManager';
import { Modal, Spinner } from 'react-bootstrap';
import {
    RiCloseLine,
    RiDeleteBinLine,
    RiFullscreenExitLine,
    RiFullscreenLine,
    RiSendPlane2Line,
} from 'react-icons/ri';
import elexerMascot from '../../Assets/Elexer_mascot_happy.png';
import elexerFullMascot from '../../Assets/Elexer_mascot_animated.gif';
import ChatMessage from './ChatMessage';
import {
    clampWindowBounds,
    getDefaultWindowBounds,
    getPointerWindowBounds,
    readWindowBounds,
    writeWindowBounds,
} from './aiChatWindowGeometry';
import { SUGGESTED_QUESTION_LANGS, SUGGESTED_QUESTIONS } from './constants';

const LONG_REQUEST_THRESHOLD_MS = 15000;
const MOBILE_BREAKPOINT = 576;
const resizeActions = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
export const SUGGEST_LANG_STORAGE_KEY = 'aiChat.suggestLang';

export function getInitialSuggestLang(
    storage = typeof window !== 'undefined' ? window.sessionStorage : null
) {
    if (!storage) return 'en';

    try {
        return storage.getItem(SUGGEST_LANG_STORAGE_KEY) === 'zh' ? 'zh' : 'en';
    } catch (error) {
        return 'en';
    }
}

export function rememberSuggestLang(
    lang,
    storage = typeof window !== 'undefined' ? window.sessionStorage : null
) {
    if (!storage) return;

    try {
        storage.setItem(SUGGEST_LANG_STORAGE_KEY, lang);
    } catch (error) {
        // The choice still applies for the current view when storage is unavailable.
    }
}

const AIChatModal = ({
    show,
    messages,
    pending,
    onHide,
    onClear,
    onSend,
    onRetry,
}) => {
    const getViewport = () => ({
        width: typeof window === 'undefined' ? 640 : window.innerWidth,
        height: typeof window === 'undefined' ? 760 : window.innerHeight,
    });
    const [input, setInput] = useState('');
    const [isLongRunning, setIsLongRunning] = useState(false);
    const [suggestLang, setSuggestLang] = useState(getInitialSuggestLang);
    const [bounds, setBounds] = useState(() => getDefaultWindowBounds(getViewport()));
    const [isMaximized, setIsMaximized] = useState(false);
    const endRef = useRef(null);
    const modalManagerRef = useRef(null);
    const pointerActionRef = useRef(null);
    const restoredBoundsRef = useRef(bounds);

    if (!modalManagerRef.current && typeof document !== 'undefined') {
        modalManagerRef.current = new ModalManager({
            ownerDocument: document,
            handleContainerOverflow: false,
        });
    }

    const suggestedQuestions = SUGGESTED_QUESTIONS[suggestLang] || SUGGESTED_QUESTIONS.en;

    const changeSuggestLang = (lang) => {
        setSuggestLang(lang);
        rememberSuggestLang(lang);
    };

    useEffect(() => {
        if (!show) return;
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, pending, show]);

    useEffect(() => {
        if (!show || typeof window === 'undefined') return;

        const nextBounds = readWindowBounds(window.localStorage, getViewport());
        restoredBoundsRef.current = nextBounds;
        setBounds(nextBounds);
        setIsMaximized(false);
    }, [show]);

    useEffect(() => {
        if (!show || typeof window === 'undefined') return undefined;

        const handleWindowResize = () => {
            if (window.innerWidth < MOBILE_BREAKPOINT || isMaximized) return;

            setBounds(current => {
                const nextBounds = clampWindowBounds(current, getViewport());
                restoredBoundsRef.current = nextBounds;
                writeWindowBounds(window.localStorage, nextBounds, getViewport());
                return nextBounds;
            });
        };

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [isMaximized, show]);

    useEffect(() => () => {
        pointerActionRef.current = null;
    }, []);

    useEffect(() => {
        if (!pending) {
            setIsLongRunning(false);
            return undefined;
        }

        const timer = window.setTimeout(
            () => setIsLongRunning(true),
            LONG_REQUEST_THRESHOLD_MS
        );
        return () => window.clearTimeout(timer);
    }, [pending]);

    const submit = () => {
        const question = input.trim();
        if (!question || pending) return;
        setInput('');
        onSend(question);
    };

    const sendSuggestion = (question) => {
        if (pending) return;
        setInput('');
        onSend(question);
    };

    const handleKeyDown = (event) => {
        if (
            event.key === 'Enter'
            && !event.shiftKey
            && !event.nativeEvent.isComposing
            && event.nativeEvent.keyCode !== 229
        ) {
            event.preventDefault();
            submit();
        }
    };

    const beginPointerAction = (event, action) => {
        if (
            typeof window === 'undefined'
            || window.innerWidth < MOBILE_BREAKPOINT
            || isMaximized
            || event.button !== 0
        ) return;

        pointerActionRef.current = {
            action,
            startBounds: bounds,
            startPoint: { x: event.clientX, y: event.clientY },
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
    };

    const movePointerAction = (event) => {
        const activeAction = pointerActionRef.current;
        if (!activeAction) return;

        setBounds(getPointerWindowBounds({
            ...activeAction,
            point: { x: event.clientX, y: event.clientY },
            viewport: getViewport(),
        }));
    };

    const endPointerAction = () => {
        if (!pointerActionRef.current || typeof window === 'undefined') return;

        pointerActionRef.current = null;
        setBounds(current => {
            const nextBounds = clampWindowBounds(current, getViewport());
            restoredBoundsRef.current = nextBounds;
            writeWindowBounds(window.localStorage, nextBounds, getViewport());
            return nextBounds;
        });
    };

    const toggleMaximize = () => {
        if (isMaximized) {
            setIsMaximized(false);
            setBounds(restoredBoundsRef.current);
            return;
        }

        restoredBoundsRef.current = bounds;
        if (typeof window !== 'undefined') {
            writeWindowBounds(window.localStorage, bounds, getViewport());
        }
        setIsMaximized(true);
    };

    const rows = Math.min(4, Math.max(1, input.split('\n').length));
    const modalStyle = {
        '--ai-chat-x': `${bounds.x}px`,
        '--ai-chat-y': `${bounds.y}px`,
        '--ai-chat-width': `${bounds.width}px`,
        '--ai-chat-height': `${bounds.height}px`,
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            scrollable
            fullscreen="sm-down"
            className={`ai-chat-modal${isMaximized ? ' is-maximized' : ''}`}
            dialogClassName="ai-chat-modal"
            style={modalStyle}
            backdrop={false}
            autoFocus={false}
            enforceFocus={false}
            restoreFocus={false}
            manager={modalManagerRef.current}
            onPointerMove={movePointerAction}
            onPointerUp={endPointerAction}
            onPointerCancel={endPointerAction}
        >
            <Modal.Header onPointerDown={event => beginPointerAction(event, 'move')}>
                <Modal.Title className="ai-chat-title">
                    <span className="ai-chat-title-icon">
                        <img
                            className="ai-chat-mascot ai-chat-mascot--title"
                            src={elexerMascot}
                            alt=""
                            aria-hidden="true"
                        />
                    </span>
                    <span className="ai-chat-title-text">
                        <span className="ai-chat-title-name">Dataco AI Assistant</span>
                        <span className="ai-chat-title-sub">Operations data &amp; analytics</span>
                    </span>
                </Modal.Title>
                <div
                    className="ai-chat-header-actions"
                    onPointerDown={event => event.stopPropagation()}
                >
                    <button
                        type="button"
                        className="ai-chat-window-control"
                        onClick={toggleMaximize}
                        aria-label={isMaximized ? 'Restore window' : 'Maximize window'}
                        title={isMaximized ? 'Restore window' : 'Maximize window'}
                    >
                        {isMaximized ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
                    </button>
                    <button
                        type="button"
                        className="ai-chat-clear"
                        onClick={onClear}
                        disabled={pending || messages.length === 0}
                        aria-label="Clear chat"
                        title="Clear chat"
                    >
                        <RiDeleteBinLine />
                    </button>
                    <button
                        type="button"
                        className="ai-chat-close"
                        onClick={onHide}
                        aria-label="Close"
                        title="Close"
                    >
                        <RiCloseLine />
                    </button>
                </div>
            </Modal.Header>

            <Modal.Body className="ai-chat-body">
                {messages.length === 0 && !pending && (
                    <div className="ai-chat-empty">
                        <div className="ai-chat-empty-icon">
                            <img
                                className="ai-chat-mascot ai-chat-mascot--empty"
                                src={elexerFullMascot}
                                alt=""
                                aria-hidden="true"
                            />
                        </div>
                        <h5>What would you like to know?</h5>
                        <p>Ask about production, quality, warehouse, inventory, sales, procurement, or finance.</p>
                        <div className="ai-chat-suggest-head">
                            <span className="ai-chat-suggest-label" id="ai-chat-suggest-label">
                                Try asking
                            </span>
                            <div
                                className="ai-chat-lang-toggle"
                                role="group"
                                aria-label="Suggested question language"
                            >
                                {SUGGESTED_QUESTION_LANGS.map(lang => (
                                    <button
                                        type="button"
                                        key={lang.id}
                                        aria-pressed={suggestLang === lang.id}
                                        onClick={() => changeSuggestLang(lang.id)}
                                    >
                                        {lang.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div
                            className="ai-chat-suggestions"
                            role="group"
                            aria-labelledby="ai-chat-suggest-label"
                        >
                            {suggestedQuestions.map(question => (
                                <button
                                    type="button"
                                    key={question}
                                    onClick={() => sendSuggestion(question)}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="ai-chat-messages" aria-live="polite">
                    {messages.map(message => (
                        <ChatMessage
                            key={message.id}
                            message={message}
                            onRetry={onRetry}
                            onSuggestion={sendSuggestion}
                            pending={pending}
                            suggestLang={suggestLang}
                        />
                    ))}

                    {pending && (
                        <div className="ai-chat-message ai-chat-message--assistant">
                            <span className="ai-chat-avatar" aria-hidden="true">
                                <img src={elexerMascot} alt="" />
                            </span>
                            <div className="ai-chat-typing" role="status">
                                <span className="ai-chat-typing-dots" aria-hidden="true">
                                    <i />
                                    <i />
                                    <i />
                                </span>
                                <span>
                                    {isLongRunning
                                        ? 'Still working — complex queries can take a minute…'
                                        : 'Analyzing your question…'}
                                </span>
                            </div>
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
            </Modal.Body>

            <Modal.Footer className="ai-chat-footer">
                <div className="ai-chat-composer">
                    <textarea
                        className="ai-chat-input"
                        value={input}
                        onChange={event => setInput(event.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={rows}
                        maxLength={5000}
                        disabled={pending}
                        placeholder="Ask Dataco AI a question…"
                        aria-label="Question for Dataco AI"
                    />
                    <button
                        type="button"
                        className="ai-chat-send"
                        onClick={submit}
                        disabled={pending || !input.trim()}
                        aria-label={pending ? 'Question is being analyzed' : 'Send question'}
                    >
                        {pending ? <Spinner size="sm" animation="border" /> : <RiSendPlane2Line />}
                    </button>
                </div>
            </Modal.Footer>
            {resizeActions.map(action => (
                <span
                    key={action}
                    className={`ai-chat-resize-handle ai-chat-resize-handle--${action}`}
                    onPointerDown={event => beginPointerAction(event, action)}
                    aria-hidden="true"
                />
            ))}
        </Modal>
    );
};

export default AIChatModal;
